import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {FacadeService} from "../../shared/services/facade.service";
import {
  ApplicantControllerService,
  ApplicantRequest,
  ApplicationLogDetailsRequest,
  ApplicationLogStepRequest,
  ApplicantAddress
} from "leia-api";
import {ApplicationService} from "../application.service";
import {CustomValidators} from "../../shared/validators/custom-validator";
import {OnboardingRequest} from "../../shared/models/onboarding-request.model";
import {OnboardingApplicant} from "../../shared/models/onboarding-applicant.model";
import {ApplicantType} from "../../shared/models/applicant-type";
import {CustomerControllerService, GetCustomerRequest} from "skyhopper-api";
import {UtilitiesService} from "../../core/services/utilities.service";
import {formatDate} from "@angular/common";
import {Subject} from "rxjs";
import {Log} from "../../shared/models/application";
import {EmploymentDetailsEmptyData, TellUsAboutYourselfEmptyData} from "../../shared/models/form-data";
import {FormState} from "../../core/interfaces/form-state";
import {State} from "../../shared/models/state";
import {
  ADULT_APPLICANT_LABEL,
  MINOR_APPLICANT_LABEL,
  PRIMARY_APPLICANT_LABEL,
  SECONDARY_APPLICANT_LABEL
} from "../../shared/constants";
import OnboardingSourceEnum = ApplicationLogStepRequest.OnboardingSourceEnum;
import {AddressType} from "../../shared/models/address-type.enum";


@Injectable({
  providedIn: 'root'
})
export class ApplicantsService {

  tempStateList: State[] = [];
  duplicatedDataErrors$: Subject<ValidationErrors> = new Subject();
  private _isSecondaryApplicantMarkedForRemoval = false;

  /**
   * Constructor
   * @param applicantService Application service instance.
   * @param logger Logger instance.
   */
  constructor(private formBuilder: FormBuilder,
              private facadeService: FacadeService,
              private applicationService: ApplicationService,
              private customerControllerService: CustomerControllerService,
              private utils: UtilitiesService,
              @Inject(LOCALE_ID) private locale: string,
              private leiaApplicantService: ApplicantControllerService,
  ) {
  }

  loadTellUsAboutYourselfFormGroup(applicant: ApplicantRequest, isMinor: boolean): FormGroup {
    const {
      applicantId, primary, firstName, middleName, lastName, suffix, ssn, dateOfBirth, phoneNumber, email, nickname
    } = applicant;

    const {
      streetAddress, streetAddress1, city, state, zip
    } = applicant.addresses?.find(address => address.addressType === AddressType.Physical)!;

    const form = this._createTellUsAboutYourselfFormGroup(
      applicantId!,
      primary!,
      isMinor,
      firstName!,
      middleName!,
      lastName!,
      suffix!,
      ssn!,
      dateOfBirth!,
      streetAddress!,
      streetAddress1!,
      city!,
      this.facadeService.loadAllStates().then((states) => {
        return states.find(data => data.stateAbbr === state)?.id ?? "Not Found";
      }),
      zip!,
      phoneNumber!,
      email!,
      nickname!
    );

    const ssnNumberControl: FormControl = (form.get("ssnNumber") as FormControl);
    ssnNumberControl.valueChanges.subscribe(changes => {
      this.taxIdTouched(ssnNumberControl, Number(applicantId), form);
    });

    return form;
  }

  onValidTellUsAboutYourself(form: FormGroup): void {
    if (form.status !== FormState.VALID) {
      return;
    }

    const applicationRequest = this.applicationService.applicationLogsRequest;
    const onboardingRequest = this.applicationService.onboardingRequest;
    const {
      id, isPrimary, firstName, middleName, lastName, suffix, ssnNumber, birthDate, addressLine1,
      addressLine2, city, state, zipCode, phoneNumber, emailAddress, preferredName
    } = form.value;
    const applicant = applicationRequest.applicants!.find(applicant => applicant.applicantId === id)!;
    applicant.primary = isPrimary;
    applicant.firstName = firstName.trim();
    applicant.middleName = middleName.trim();
    applicant.lastName = lastName.trim();
    applicant.suffix = suffix;
    applicant.ssn = ssnNumber.replace(/\-/gi, "");
    applicant.dateOfBirth = formatDate(birthDate, "yyyy-MM-dd", this.locale);

    applicant.addresses = [];
    let applicantAddress = new ApplicantAddress();

    applicantAddress.state = this.getStateId(state);
    applicantAddress.streetAddress = addressLine1;
    applicantAddress.streetAddress1 = addressLine2;
    applicantAddress.city = city;
    applicantAddress.zip = zipCode;
    applicantAddress.addressType = AddressType.Physical;

    applicant.addresses.push(applicantAddress);

    applicant.phoneNumber = phoneNumber.replace(/\-/gi, "");
    applicant.email = emailAddress;
    applicant.nickname = preferredName;

    const onboardingApplicant = onboardingRequest.applicants.find(applicant => applicant.applicantId === id)!;
    onboardingApplicant.tellUsAboutYourself.firstName = firstName.trim();
    onboardingApplicant.tellUsAboutYourself.middleName = middleName.trim();
    onboardingApplicant.tellUsAboutYourself.lastName = lastName.trim();
    onboardingApplicant.tellUsAboutYourself.suffix = suffix;
    onboardingApplicant.tellUsAboutYourself.ssnNumber = ssnNumber;
    onboardingApplicant.tellUsAboutYourself.birthDate = birthDate;
    onboardingApplicant.tellUsAboutYourself.addressLine1 = addressLine1;
    onboardingApplicant.tellUsAboutYourself.addressLine2 = addressLine2;
    onboardingApplicant.tellUsAboutYourself.city = city;
    onboardingApplicant.tellUsAboutYourself.zipcode = zipCode;
    onboardingApplicant.tellUsAboutYourself.phoneNumber = phoneNumber;
    onboardingApplicant.tellUsAboutYourself.email = emailAddress;
    onboardingApplicant.tellUsAboutYourself.state = applicant.addresses.find(address => address.addressType === AddressType.Physical)?.state!;
    onboardingApplicant.tellUsAboutYourself.stateId = state;
    onboardingApplicant.tellUsAboutYourself.preferredName = preferredName;
    this.applicationService._updateApplication(
      applicationRequest,
      onboardingRequest
    );
    //TODO - mparticle?

  }

  onInvalidTellUsAboutYourself(form: FormGroup): void {
    if (form.dirty) {
      console.log('Personal Details Not Complete')
    } else {
      console.log('Personal Details Complete')
    }
  }

  createTellUsAboutYourselfFormGroup(isPrimary: boolean, isMinor: boolean): Promise<FormGroup> {
    const applicationLogRequest = this.applicationService.applicationLogsRequest;
    const onboardingRequest = this.applicationService.onboardingRequest;
    if (isPrimary) {
      this.getStateList();
    }
    //Set the application is joint or single by checking the primary/minor flag. 0-Single, 1 - Joint
    if (!isPrimary || isMinor) applicationLogRequest.isJoint = 1;
    return new Promise(resolve => {
      let id = isPrimary && !isMinor ? 1 : 2;
      if (!Array.isArray(applicationLogRequest.applicants)) {
        applicationLogRequest.applicants = [];
      }

      if (!Array.isArray(onboardingRequest.applicants)) {
        onboardingRequest.applicants = [];
      }

      applicationLogRequest.applicants.push({applicantId: id});

      const onboardingApplicant = new OnboardingApplicant();
      onboardingApplicant.applicantId = id;
      onboardingApplicant.applicantType = isPrimary ? ApplicantType.Primary : ApplicantType.Secondary;
      onboardingApplicant.isMinor = false;
      onboardingRequest.applicants.push(onboardingApplicant);

      this.applicationService.applicationLogsRequest = applicationLogRequest;
      this.applicationService.onboardingRequest = onboardingRequest;

      var {
        firstName,
        middleName,
        lastName,
        suffix,
        birthDate,
        addressLine1,
        addressLine2,
        city,
        state,
        zipCode,
        phoneNumber,
        ssnNumber,
        email,
        preferredName
      } = TellUsAboutYourselfEmptyData;


      const form = this._createTellUsAboutYourselfFormGroup(
        id, isPrimary, isMinor, firstName, middleName, lastName, suffix, ssnNumber, birthDate, addressLine1, addressLine2,
        city, state, zipCode, phoneNumber, email, preferredName
      );

      const ssnNumberControl: FormControl = (form.get("ssnNumber") as FormControl);
      ssnNumberControl.valueChanges.subscribe(changes => {
        this.taxIdTouched(ssnNumberControl, id, form);
      });

      resolve(form);
    });
  }

  loadEmploymentDetailsFormGroup(applicant: ApplicantRequest, isMinor: boolean): FormGroup {
    const {
      applicantId,
      primary,
      annualIncome,
      employerCountry,
      occupation,
      primarySourceOfFunds
    } = applicant;

    return this._createEmploymentDetailsFormGroup(
      applicantId!,
      primary!,
      isMinor,
      Number(primarySourceOfFunds!),
      Number(occupation!),
      Number(employerCountry!),
      Number(annualIncome!)
    )
  }

  createEmploymentDetailsFormGroup(id: number, isPrimary: boolean, isMinor: boolean): FormGroup {
    var {
      annualIncome,
      employerCountry,
      incomeSource,
      occupation
    } = EmploymentDetailsEmptyData;

    return this._createEmploymentDetailsFormGroup(
      id, isPrimary, isMinor, incomeSource, occupation, employerCountry, annualIncome
    );
  }

  onValidEmploymentDetails(form: FormGroup): void {
    if (form.status !== FormState.VALID) {
      return;
    }
    const applicationRequest = this.applicationService.applicationLogsRequest;
    const onboardingRequest = this.applicationService.onboardingRequest;
    const {id, incomeSource, occupation, employerCountry, annualIncome} = form.value;

    const applicant = applicationRequest.applicants!.find(applicant => applicant.applicantId === id)!;
    applicant.primarySourceOfFunds = incomeSource;
    applicant.occupation = occupation;
    applicant.employerCountry = employerCountry;
    applicant.annualIncome = annualIncome;

    const onboardingApplicant = onboardingRequest.applicants.find(applicant => applicant.applicantId === id)!;
    onboardingApplicant.employmentDetails.annualIncomeId = annualIncome;
    onboardingApplicant.employmentDetails.countryId = employerCountry;
    onboardingApplicant.employmentDetails.annualIncomeId = annualIncome;
    onboardingApplicant.employmentDetails.occupationId = occupation;
    this.applicationService._updateApplication(
      applicationRequest,
      onboardingRequest
    );
  }

  onInvalidEmploymentDetails(form: FormGroup): void {
    if (form.dirty) {
      console.log('Employment Not Complete')
    } else {
      console.log('Employment Complete')
    }
  }

  /**
   * Make customer profile call, set the profile details to the form.
   * @param formControl
   * @param applicantId
   * @param form
   */
  taxIdTouched(formControl: FormControl, applicantId: Number, form: FormGroup): void {
    // if the field isn't changed or doesn't pass validation, do nothing
    if (
      formControl.pristine ||
      formControl.invalid
    ) {
      return;
    }
    let applicationLogRequest = this.applicationService.applicationLogsRequest;
    let onboardingRequest = this.applicationService.onboardingRequest;
    const logs: Log[] = [];
    const logDetails: ApplicationLogDetailsRequest[] = [];

    let applicantContext = this._getApplicantById(onboardingRequest, applicantId)!;

    if (!applicantContext) {
      logs.push(this.utils.createLog("Coruscant", "Unable to perform Customer Profile check. Customer information doesn't exist."));
      logDetails.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.CORUSCANT, "Unable to perform Customer Profile check. Customer information doesn't exist."));

      this.applicationService._updateApplication(
        applicationLogRequest,
        onboardingRequest
      );
    } else {
      // determine if this is the primary applicant or not
      let isPrimary = applicantContext.applicantType === ApplicantType.Primary;
      if (applicationLogRequest.applicants?.length == 1) {
        isPrimary = true;
      }
      const customerRequest = new GetCustomerRequest();
      customerRequest.ssn = formControl.value.replace(/-/gi, "");
      customerRequest.sessionId = this.applicationService.id;
      const last4 = customerRequest.ssn!.substring(5);

      this.customerControllerService.getCustomerProfile(customerRequest).subscribe(response => {

        applicationLogRequest = this.applicationService.applicationLogsRequest;
        onboardingRequest = this.applicationService.onboardingRequest;
        applicantContext = this._getApplicantById(onboardingRequest, applicantId)!;

        logs.push(this.utils.createLog("SKH", JSON.stringify(response)));
        logDetails.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.SKYHOPPER, JSON.stringify(response)));

        if (response.responseContent === null) {
          logs.push(this.utils.createLog("Coruscant", `Customer with SSN XXX-XX-${last4} not found.`));
          logDetails.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.CORUSCANT, `Customer with SSN XXX-XX-${last4} not found.`));
          return;
        }
        const {firstName, lastName, emailAddress, birthDate} = response.responseContent;
        const {street, houseNumber, city, state, zipCode} = response.responseContent.address[0];
        const phoneNumber = response.responseContent.phoneNumber[0].number;
        const setFormValue = (name: string, value: any): void => {
          const control = form.controls[name];
          if (value!) {
            if (name === 'birthDate') {
              control.setValue(this._createDateObject(value));
            } else {
              control.setValue(value);
            }
            control.markAsTouched();
          }
        }
        setFormValue('firstName', firstName);
        setFormValue('lastName', lastName);
        setFormValue('emailAddress', emailAddress);
        setFormValue('birthDate', birthDate);
        setFormValue('phoneNumber', phoneNumber);
        setFormValue('addressLine1', houseNumber ? houseNumber + ' ' + street : street);
        setFormValue('addressLine2', '');
        setFormValue('city', city);
        setFormValue('state', this.getState(state));
        setFormValue('zipCode', zipCode);
        // map new fields to application log request
        form.updateValueAndValidity();
        applicationLogRequest.applicants?.forEach((applicant, index) => {
          if (applicant.applicantId === applicantContext.applicantId) {
            applicant.firstName = firstName;
            applicant.lastName = lastName;
            applicant.ssn = formControl.value.replace(/-/gi, "");
            applicant.email = emailAddress;
            applicant.dateOfBirth = birthDate;

            applicant.addresses = [];
            let applicantAddress = new ApplicantAddress();

            applicantAddress.state = state;
            applicantAddress.streetAddress = houseNumber ? houseNumber + ' ' + street : street;
            applicantAddress.streetAddress1 = '';
            applicantAddress.city = city;
            applicantAddress.zip = zipCode;

            //TODO: Revisit
            applicantAddress.addressType = AddressType.Physical;

            applicant.addresses.push(applicantAddress);

            applicant.phoneNumber = phoneNumber;
            applicant.primary = isPrimary;
            applicant.ssn = customerRequest.ssn;
          }
        });
        this.applicationService._updateApplication(
          applicationLogRequest,
          onboardingRequest
        );
      }, error => {
        logs.push(this.utils.createLog("SKH", JSON.stringify(error)));
        logs.push(this.utils.createLog("Coruscant", "Customer Profile check failed."));

        logDetails.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.SKYHOPPER, JSON.stringify(error)));
        logDetails.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.CORUSCANT, "Customer Profile check failed."))

        // set applicant fields
        applicantContext.customerInfo.checkSucceeded = false;

        onboardingRequest.applicants.forEach((applicant, index) => {
          if (applicant === applicantContext) {
            onboardingRequest.applicants[index] = applicantContext;
          }
        })
      });
    }
  }

  sendDuplicatedDataErrors(errors: ValidationErrors): void {
    this.duplicatedDataErrors$.next(errors);
  }

  markSecondaryApplicantForRemoval(): void {
    this._isSecondaryApplicantMarkedForRemoval = true;
  }

  unmarkSecondaryApplicantForRemoval(): void {
    this._isSecondaryApplicantMarkedForRemoval = false;
  }

  removeSecondaryApplicantIfNecessary(formToRemoveFrom?: FormArray): void {
    if (!this._isSecondaryApplicantMarkedForRemoval) {
      return;
    }

    const applicationLogRequest = this.applicationService.applicationLogsRequest;
    const onboardingRequest = this.applicationService.onboardingRequest;

    const applicants = applicationLogRequest.applicants!;

    if (applicants.length === 1) {
      return;
    }

    //const applicantId = applicants.find(applicant => !applicant.primary)!.applicantId;
    const applicantId = onboardingRequest.applicants.find(applicant => applicant.applicantType! === ApplicantType.Secondary)?.applicantId;
    applicationLogRequest.applicants?.splice(
      applicationLogRequest.applicants?.findIndex(applicant => applicant.applicantId === applicantId));

    onboardingRequest.applicants.splice(
      onboardingRequest.applicants.findIndex(applicant => applicant.applicantId === applicantId));
    //Set the flag as 0 (Single Application) , 0 - Single, 1 - Joint
    applicationLogRequest.isJoint = 0;

    this.applicationService._updateApplication(
      applicationLogRequest,
      onboardingRequest
    );

    if (formToRemoveFrom && formToRemoveFrom.length > 1) {
      const formValues = (formToRemoveFrom.value as any[]);
      const applicantToDeleteIndex = formValues.findIndex(applicant => !applicant.tellUsAboutYourself.isPrimary);
      formToRemoveFrom.removeAt(applicantToDeleteIndex);
    }
  }

  getStateId(state: number): string {
    let stateAbbrev = 'Not Found';
    this.tempStateList.find(data => {
      if (data.id === state) {
        stateAbbrev = data.stateAbbr;
      }
    })
    return stateAbbrev;
  }

  /**
   * Get the list of states from the service.
   */
  getStateList(): void {
    // Call the Falcon Service to get income sources
    this.facadeService.loadAllStates().then((states) => {
      this.tempStateList = states;
    });
  }

  private _createTellUsAboutYourselfFormGroup(
    id: number, isPrimary: boolean, isMinor: boolean, firstName: string, middleName: string, lastName: string, suffix: string, ssnNumber: string, birthDate: string,
    addressLine1: string, addressLine2: string, city: string, state: any, zipCode: string, phoneNumber: string,
    email: string, preferredName: string
  ): FormGroup {
    const birthDateObject = this._createDateObject(birthDate!);
    let applicantType = isPrimary ? PRIMARY_APPLICANT_LABEL : SECONDARY_APPLICANT_LABEL;
    if (isMinor) {
      applicantType = isPrimary ? MINOR_APPLICANT_LABEL : ADULT_APPLICANT_LABEL;
    }
    return this.formBuilder.group({
      id: id,
      isPrimary: [isPrimary],
      applicantType: [applicantType],
      firstName: [firstName, [Validators.required, Validators.minLength(1), Validators.maxLength(100), CustomValidators.applicantFirstNameSpecialCharacterValidator]],
      middleName: [middleName, [Validators.maxLength(100), CustomValidators.applicantMiddleNameSpecialCharacterValidator]],
      lastName: [lastName, [Validators.required, Validators.minLength(1), Validators.maxLength(100), CustomValidators.applicantLastNameSpecialCharacterValidator, CustomValidators.applicantLastNameTrustValidation]],
      suffix: [suffix, []],
      ssnNumber: [ssnNumber, [Validators.required, CustomValidators.socialSecurityNumberValidator]],
      birthDate: [birthDateObject, [Validators.required, Validators.maxLength(7)]],
      addressLine1: [addressLine1, [Validators.required, Validators.minLength(2), Validators.maxLength(50), CustomValidators.applicantAddressLine1SpecialCharacterValidator, CustomValidators.AddressLine1Validator, CustomValidators.POBoxValidator({hasPOBox: true})]],
      addressLine2: [addressLine2, [Validators.maxLength(50), CustomValidators.POBoxValidator({hasPOBox: true}), CustomValidators.applicantAddressLine2SpecialCharacterValidator]],
      city: [city, [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
      state: [state, [Validators.required, Validators.minLength(0), Validators.pattern("^[1-9][0-9]*$")]],
      zipCode: [zipCode, [Validators.required, Validators.minLength(4), Validators.maxLength(10)]],
      phoneNumber: [phoneNumber, [Validators.required, Validators.minLength(9), Validators.maxLength(12), CustomValidators.PhoneNumberValidation]],
      emailAddress: [email, [Validators.required, Validators.email, Validators.minLength(2), Validators.maxLength(100), CustomValidators.emailValidator]],
      preferredName: [preferredName, [Validators.maxLength(50)]],
    }, {updateOn: "blur"});
  }

  /**
   * Update email/phone & employment field validator for Minor savings product.
   * @param appFormArray
   * @param isMinor
   */
  updateFormValidators(appFormArray: FormArray, isMinor: boolean) {
    for (let appForm of appFormArray.controls) {
      let applicantType = appForm.value.tellUsAboutYourself.isPrimary ? PRIMARY_APPLICANT_LABEL : SECONDARY_APPLICANT_LABEL;
      const applicantTypeControl: FormControl = (appForm.get("tellUsAboutYourself.applicantType") as FormControl);
      const employmentApplicantTypeControl: FormControl = (appForm.get("employmentDetails.applicantType") as FormControl);
      if (appForm.value.tellUsAboutYourself.isPrimary) {
        const emailAddressControl: FormControl = (appForm.get("tellUsAboutYourself.emailAddress") as FormControl);
        const phoneNumberControl: FormControl = (appForm.get("tellUsAboutYourself.phoneNumber") as FormControl);
        const incomeSourceControl: FormControl = (appForm.get("employmentDetails.incomeSource") as FormControl);
        const occupationControl: FormControl = (appForm.get("employmentDetails.occupation") as FormControl);
        const employerCountryControl: FormControl = (appForm.get("employmentDetails.employerCountry") as FormControl);
        const annualIncomeControl: FormControl = (appForm.get("employmentDetails.annualIncome") as FormControl);

        if (isMinor) {
          applicantType = MINOR_APPLICANT_LABEL;
          emailAddressControl.clearValidators();
          phoneNumberControl.clearValidators();
          emailAddressControl.setValidators([Validators.email, Validators.minLength(3), Validators.maxLength(100)]);
          phoneNumberControl.setValidators([Validators.minLength(9), Validators.maxLength(12)]);
          incomeSourceControl.clearValidators();
          occupationControl.clearValidators();
          employerCountryControl.clearValidators();
          annualIncomeControl.clearValidators();
        } else {
          emailAddressControl.setValidators([Validators.email, Validators.minLength(3), Validators.maxLength(100), Validators.required]);
          phoneNumberControl.setValidators([Validators.minLength(9), Validators.maxLength(12), Validators.required]);
          incomeSourceControl.setValidators([Validators.required, Validators.min(1)])
          occupationControl.setValidators([Validators.required, Validators.min(1)])
          employerCountryControl.setValidators([Validators.required, Validators.min(1)])
          annualIncomeControl.setValidators([Validators.required, Validators.min(1)])
        }
        appForm.updateValueAndValidity();

      } else {
        if (isMinor) applicantType = ADULT_APPLICANT_LABEL;
      }
      applicantTypeControl.setValue(applicantType);
      appForm.updateValueAndValidity();
    }
  }

  private _createEmploymentDetailsFormGroup(
    id: number, isPrimary: boolean, isMinor: boolean, incomeSource: number, occupation: number,
    employerCountry: number, annualIncome: number
  ): FormGroup {
    let applicantType = isPrimary ? PRIMARY_APPLICANT_LABEL : SECONDARY_APPLICANT_LABEL;
    if (isMinor) {
      applicantType = isPrimary ? MINOR_APPLICANT_LABEL : ADULT_APPLICANT_LABEL;
    }
    return this.formBuilder.group({
      id: id,
      isPrimary: [isPrimary],
      applicantType: [applicantType],
      incomeSource: [incomeSource, [Validators.required, Validators.min(1)]],
      occupation: [occupation, [Validators.required, Validators.min(1)]],
      employerCountry: [employerCountry, [Validators.required, Validators.min(1)]],
      annualIncome: [annualIncome, [Validators.required, Validators.min(1)]]
    });
  }

  private _getApplicantById(onboardingRequest: OnboardingRequest, id: Number): OnboardingApplicant | undefined {
    return onboardingRequest.applicants.find(applicant => applicant.applicantId === id);
  }

  private _createDateObject(humanReadableDate: Date | string): Date | string {
    if (!!!humanReadableDate) {
      return "";
    }

    if (typeof (humanReadableDate) === "object") {
      return humanReadableDate;
    }

    const dateParts = humanReadableDate.split("-");
    return new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
  }

  /**
   * Get the state id given an Name.
   * @param stateId
   */
  getState(stateAbbrev: string | undefined): number {
    return this.tempStateList.find(state => state.stateAbbr === stateAbbrev)?.id || 0;
  }
}
